"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@astryxdesign/core/Icon";
import { Text } from "@astryxdesign/core/Text";
import {
  filterLandingIconOptions,
  getLandingIconLabel,
  LANDING_ICON_OPTIONS,
  parseLandingIcon,
} from "@/lib/landing/puck/icon-options";
import { researchIcons } from "@/lib/icons/research-icons";

type LandingIconPickerFieldProps = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
};

export function LandingIconPickerField({
  value,
  onChange,
  label = "Иконка",
}: LandingIconPickerFieldProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = parseLandingIcon(value);
  const selectedLabel = getLandingIconLabel(selected);
  const options = useMemo(() => filterLandingIconOptions(query), [query]);

  useEffect(() => {
    if (!open) return;

    const onDocPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [open]);

  function pick(next: string) {
    onChange(next);
    setOpen(false);
    setQuery("");
  }

  return (
    <div
      ref={rootRef}
      className="w-full"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <Text size="sm" color="secondary" className="mb-1.5 block">
        {label}
      </Text>

      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        className={`flex w-full items-center gap-2 rounded-md border bg-canvas px-2 py-1.5 text-left transition-colors ${
          open ? "border-primary ring-1 ring-primary/30" : "border-border hover:bg-muted"
        }`}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border border-border bg-muted">
          <Icon icon={researchIcons[selected]} size="sm" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-primary">
            {selectedLabel}
          </span>
          <span className="block truncate text-[11px] text-secondary">{selected}</span>
        </span>
        <span
          className={`inline-flex shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
        >
          <Icon icon={researchIcons.chevronRight} size="sm" color="secondary" />
        </span>
      </button>

      {open ? (
        <div className="mt-1 overflow-hidden rounded-md border border-border bg-canvas shadow-sm">
          <div className="flex items-center gap-2 border-b border-border px-2 py-1.5">
            <Icon icon={researchIcons.search} size="sm" color="secondary" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск…"
              className="min-w-0 flex-1 bg-transparent text-sm text-primary outline-none placeholder:text-secondary"
              autoFocus
            />
            {query ? (
              <button
                type="button"
                className="inline-flex h-5 w-5 items-center justify-center rounded text-secondary hover:bg-muted"
                aria-label="Очистить поиск"
                onClick={() => setQuery("")}
              >
                <Icon icon={researchIcons.close} size="sm" />
              </button>
            ) : null}
          </div>

          <div
            className="grid max-h-44 grid-cols-6 gap-0.5 overflow-y-auto p-1.5"
            role="listbox"
            aria-label={label}
          >
            {options.length === 0 ? (
              <p className="col-span-6 px-2 py-3 text-center text-xs text-secondary">
                Ничего не найдено
              </p>
            ) : (
              options.map((opt) => {
                const active = opt.value === selected;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={active}
                    title={opt.label}
                    className={`inline-flex h-8 w-full items-center justify-center rounded transition-colors ${
                      active
                        ? "bg-primary/10 text-primary ring-1 ring-primary"
                        : "text-secondary hover:bg-muted hover:text-primary"
                    }`}
                    onClick={() => pick(opt.value)}
                  >
                    <Icon icon={researchIcons[opt.value]} size="sm" />
                  </button>
                );
              })
            )}
          </div>

          <p className="border-t border-border px-2 py-1 text-[10px] text-secondary">
            {options.length} из {LANDING_ICON_OPTIONS.length}
          </p>
        </div>
      ) : null}
    </div>
  );
}
